'use client';
import React, { useState, useEffect } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(ios);

    // Check if dismissed before
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) return;

    if (ios) {
      // Show iOS instructions after a short delay
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', '1');
  };

  if (!showPrompt || isInstalled) return null;

  return (
    <div className="fixed bottom-20 lg:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 z-50 animate-slide-up">
      <div className="bg-card border border-primary/30 rounded-2xl shadow-2xl p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/15 border border-primary/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <Download size={18} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-700 text-foreground">Install MyDoQs</p>
              <p className="text-xs text-muted-foreground">Add to your home screen</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors mt-0.5"
            aria-label="Dismiss install prompt"
          >
            <X size={16} />
          </button>
        </div>

        {isIOS ? (
          <div className="bg-muted/50 rounded-xl p-3 mb-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Tap the{' '}
              <span className="inline-flex items-center gap-0.5 text-primary font-600">
                <Share size={12} /> Share
              </span>{' '}
              button, then{' '}
              <span className="inline-flex items-center gap-0.5 text-primary font-600">
                <Plus size={12} /> Add to Home Screen
              </span>
            </p>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground font-600 text-sm rounded-xl hover:bg-primary/90 transition-colors mb-2"
          >
            <Download size={15} />
            Install App
          </button>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Works offline · No app store required
        </p>
      </div>
    </div>
  );
}
